import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { aiSearch, getAIConsultantVideos, getAIToolsStatus, getMyCredits } from '../lib/api';
import { usePageTracking, useAuditLog } from '../hooks/useAuditLog';
import { 
  ArrowLeft, 
  Send, 
  Sparkles, 
  User,
  Copy,
  Check,
  RotateCcw,
  ThumbsUp,
  ThumbsDown,
  Play,
  FileText,
  ChevronDown,
  Loader2,
  MessageSquare,
  Building2,
  Globe,
  BotMessageSquare,
  ShieldOff,
  Coins
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_valuebot/artifacts/etd4vs0l_Absolin_logo_Blue-HD.png";
const AI_CONSULTANT_ICON = "https://static.prod-images.emergentagent.com/jobs/cedee861-1d2e-4012-91c4-57a04e6b2415/images/1301e85db0f0fc2729ff63b21033bb734b2a1ed13c96fb36cc9aaba97439f25f.png";

// Language options for AI Chat
const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English', flag: '🇬🇧' }
];

// Suggested prompts for users
const SUGGESTED_PROMPTS = [
  "What ERP modules do you offer for manufacturing?",
  "How does ABSOLIN handle GST compliance?",
  "Tell me about your inventory management features",
  "What makes ABSOLIN different from SAP?",
  "How long does implementation typically take?",
  "Share a success story in retail industry"
];

export default function AISearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [videos, setVideos] = useState([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [sessionId, setSessionId] = useState(''); // P1: Chat session ID
  const [verticalContext, setVerticalContext] = useState(null); // P1: Current vertical context
  const [selectedLanguage, setSelectedLanguage] = useState('en'); // P0: Multi-language support
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [credits, setCredits] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Audit logging
  usePageTracking('AI Consultant');
  const { logAIQuestion } = useAuditLog();

  // Check if AI Consultant is enabled
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await getAIToolsStatus();
        setIsDisabled(!res.data.ai_consultant_enabled);
      } catch { setIsDisabled(false); }
      finally { setCheckingStatus(false); }
    };
    const loadCredits = async () => {
      try {
        const res = await getMyCredits();
        setCredits(res.data.credits);
      } catch { }
    };
    checkStatus();
    loadCredits();
  }, []);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [query, adjustTextareaHeight]);

  // Scroll handling
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      setShowScrollButton(scrollHeight - scrollTop - clientHeight > 100);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Load videos
  useEffect(() => {
    const loadVideos = async () => {
      try {
        const response = await getAIConsultantVideos();
        setVideos(response.data || []);
      } catch (error) {
      }
    };
    loadVideos();
  }, []);

  const handleSend = async (customQuery = null) => {
    const messageText = customQuery || query.trim();
    if (!messageText || isLoading) return;

    setQuery('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    // Log AI question
    logAIQuestion(messageText, 'consultant');

    try {
      const conversationHistory = messages.map(m => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.content
      }));

      // P1: Pass session ID and vertical context for personalization
      // P0: Pass language for multi-language support
      const response = await aiSearch(messageText, conversationHistory, sessionId, verticalContext?.id, selectedLanguage);
      const { answer, sources, suggested_problems, suggested_videos, session_id: returnedSessionId, vertical_context, language } = response.data;

      // P1: Update session ID for continuity
      if (returnedSessionId && !sessionId) {
        setSessionId(returnedSessionId);
      }
      
      // P1: Update vertical context if returned
      if (vertical_context) {
        setVerticalContext(vertical_context);
      }

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: answer,
        sources: sources || [],
        suggestedProblems: suggested_problems || [],
        suggestedVideos: suggested_videos || videos.slice(0, 2),
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      // Refresh credits
      try { const cr = await getMyCredits(); setCredits(cr.data.credits); } catch {}
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to get response';
      const isCredits = errorMsg.includes("credits") || errorMsg.includes("No credits");
      toast.error(isCredits ? "No credits remaining. Contact your admin." : errorMsg);
      
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: isCredits
          ? "You've run out of AI credits. Please contact your administrator to get more credits."
          : "I apologize, but I'm having trouble processing your request. Please try again or contact ABSOLIN directly for assistance.",
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleRegenerate = (messageIndex) => {
    const userMessage = messages[messageIndex - 1];
    if (userMessage?.role === 'user') {
      setMessages(prev => prev.slice(0, messageIndex));
      handleSend(userMessage.content);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setQuery('');
    setSessionId(''); // P1: Reset session for new chat
    setVerticalContext(null); // P1: Reset vertical context
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (isDisabled) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center" data-testid="ai-consultant-disabled">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShieldOff className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">AI Consultant is Disabled</h2>
          <p className="text-slate-500 text-sm mb-6">This agent has been temporarily disabled by the administrator. Please check back later or contact support.</p>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb] flex flex-col" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              data-testid="ai-search-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img 
                src={AI_CONSULTANT_ICON} 
                alt="AI Consultant" 
                className="w-8 h-8 object-contain"
              />
              <span className="font-semibold text-gray-900">AI Consultant</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* P0: Multi-language Selector */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                className="text-gray-500 hover:text-gray-700 gap-2"
                data-testid="language-selector-btn"
              >
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.flag}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>
              
              {showLanguageMenu && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px] z-50">
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setSelectedLanguage(lang.code);
                        setShowLanguageMenu(false);
                        toast.success(`Language changed to ${lang.name}`);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${
                        selectedLanguage === lang.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      data-testid={`language-option-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                      {selectedLanguage === lang.code && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewChat}
                className="text-gray-500 hover:text-gray-700 gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                New Chat
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto"
      >
        {messages.length === 0 ? (
          /* Empty State - Welcome Screen */
          <div className="max-w-3xl mx-auto px-4 py-12">
            <div className="text-center mb-12">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/10 p-2">
                <img src={AI_CONSULTANT_ICON} alt="AI Consultant" className="w-12 h-12 object-contain" />
              </div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                How can I help you today?
              </h1>
              <p className="text-gray-500 max-w-md mx-auto">
                I&apos;m ABSOLIN&apos;s AI Consultant. Ask me about ERP solutions, business challenges, implementation, or anything about digital transformation.
              </p>
            </div>

            {/* Suggested Prompts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SUGGESTED_PROMPTS.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSend(prompt)}
                  className="group p-4 text-left bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/50 transition-all duration-200"
                  data-testid={`suggested-prompt-${idx}`}
                >
                  <p className="text-sm text-gray-700 group-hover:text-blue-700">
                    {prompt}
                  </p>
                </button>
              ))}
            </div>

            {/* Capabilities */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-white border border-gray-100">
                <Building2 className="w-5 h-5 text-blue-500 mb-2" />
                <h3 className="font-medium text-gray-900 text-sm mb-1">Industry Expertise</h3>
                <p className="text-xs text-gray-500">Manufacturing, Retail, Pharma, Steel, Seafood Export, Trading</p>
              </div>
              <div className="p-4 rounded-xl bg-white border border-gray-100">
                <FileText className="w-5 h-5 text-green-500 mb-2" />
                <h3 className="font-medium text-gray-900 text-sm mb-1">Product Knowledge</h3>
                <p className="text-xs text-gray-500">ERP, GST, Inventory, Payroll, DMS, Mobile Apps</p>
              </div>
              <div className="p-4 rounded-xl bg-white border border-gray-100">
                <Play className="w-5 h-5 text-purple-500 mb-2" />
                <h3 className="font-medium text-gray-900 text-sm mb-1">Success Stories</h3>
                <p className="text-xs text-gray-500">500+ implementations, 15+ years experience</p>
              </div>
            </div>
          </div>
        ) : (
          /* Chat Messages */
          <div className="max-w-3xl mx-auto px-4 py-6">
            {messages.map((message, idx) => (
              <div 
                key={message.id} 
                className={`mb-6 ${message.role === 'user' ? 'flex justify-end' : ''}`}
              >
                {message.role === 'user' ? (
                  /* User Message */
                  <div className="flex items-start gap-3 max-w-[85%]">
                    <div className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tr-md">
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-gray-600" />
                    </div>
                  </div>
                ) : (
                  /* Assistant Message */
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 p-1 mt-1">
                      <img src={AI_CONSULTANT_ICON} alt="" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`ai-response-content ${message.isError ? 'text-red-600' : ''}`}>
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => (
                              <p className="mb-4 text-[15px] leading-[1.7] text-slate-800">{children}</p>
                            ),
                            ul: ({ children }) => (
                              <ul className="mb-4 space-y-2 ml-1">{children}</ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="mb-4 space-y-2 ml-1 list-decimal list-inside">{children}</ol>
                            ),
                            li: ({ children }) => (
                              <li className="text-[15px] leading-[1.7] text-slate-800 flex items-start gap-2">
                                <span className="text-blue-500 mt-[6px] flex-shrink-0">•</span>
                                <span>{children}</span>
                              </li>
                            ),
                            strong: ({ children }) => (
                              <strong className="font-semibold text-slate-900">{children}</strong>
                            ),
                            em: ({ children }) => (
                              <em className="italic text-slate-700">{children}</em>
                            ),
                            code: ({ children, inline }) => 
                              inline ? (
                                <code className="px-1.5 py-0.5 bg-slate-100 rounded text-[14px] font-mono text-slate-800">{children}</code>
                              ) : (
                                <code className="block p-3 bg-slate-100 rounded-lg text-[14px] font-mono text-slate-800 my-3 overflow-x-auto">{children}</code>
                              ),
                            h1: ({ children }) => (
                              <h1 className="text-xl font-bold text-slate-900 mt-6 mb-3 pb-2 border-b border-slate-200">{children}</h1>
                            ),
                            h2: ({ children }) => (
                              <h2 className="text-lg font-bold text-slate-900 mt-5 mb-3">{children}</h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="text-base font-semibold text-slate-900 mt-4 mb-2">{children}</h3>
                            ),
                            blockquote: ({ children }) => (
                              <blockquote className="border-l-4 border-blue-500 pl-4 py-1 my-4 bg-blue-50/50 rounded-r-lg italic text-slate-700">{children}</blockquote>
                            ),
                            hr: () => (
                              <hr className="my-6 border-slate-200" />
                            ),
                            a: ({ href, children }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">{children}</a>
                            ),
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>

                      {/* Suggested Videos */}
                      {message.suggestedVideos?.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-medium text-gray-500">Related Videos</p>
                          {message.suggestedVideos.slice(0, 2).map((video, vIdx) => (
                            <a
                              key={vIdx}
                              href={video.youtube_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 p-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center">
                                <Play className="w-4 h-4 text-red-600" />
                              </div>
                              <span className="text-sm text-gray-700">{video.title}</span>
                            </a>
                          ))}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="mt-3 flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy(message.content, message.id)}
                          className="h-8 px-2 text-gray-400 hover:text-gray-600"
                        >
                          {copiedId === message.id ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRegenerate(idx)}
                          className="h-8 px-2 text-gray-400 hover:text-gray-600"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-gray-400 hover:text-gray-600"
                        >
                          <ThumbsUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-gray-400 hover:text-gray-600"
                        >
                          <ThumbsDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Loading State */}
            {isLoading && (
              <div className="flex items-start gap-3 mb-6">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center p-1">
                  <img src={AI_CONSULTANT_ICON} alt="" className="w-6 h-6 object-contain" />
                </div>
                <div className="flex items-center gap-2 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm text-gray-500">Thinking...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Scroll to Bottom Button */}
      {showScrollButton && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-28 right-8 w-10 h-10 bg-white border border-gray-200 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
        >
          <ChevronDown className="w-5 h-5 text-gray-600" />
        </button>
      )}

      {/* Input Area */}
      <div className="sticky bottom-0 bg-gradient-to-t from-[#f9fafb] via-[#f9fafb] to-transparent pt-4 pb-6">
        <div className="max-w-3xl mx-auto px-4">
          <div className="relative bg-white border border-gray-200 rounded-2xl shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
            <textarea
              ref={textareaRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about ERP solutions, business challenges..."
              disabled={isLoading}
              rows={1}
              className="w-full px-4 py-3 pr-12 bg-transparent resize-none focus:outline-none text-gray-900 placeholder:text-gray-400 text-sm max-h-48"
              data-testid="ai-search-input"
            />
            <Button
              onClick={() => handleSend()}
              disabled={isLoading || !query.trim()}
              size="icon"
              className="absolute right-2 bottom-2 w-8 h-8 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 rounded-lg transition-colors"
              data-testid="ai-search-send-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-center text-xs text-gray-400 mt-2">
            Powered by ABSOLIN Knowledge Base
            {credits !== null && (
              <span className={`ml-2 inline-flex items-center gap-1 ${credits <= 5 ? "text-red-500" : credits <= 20 ? "text-amber-500" : "text-slate-400"}`} data-testid="credits-remaining">
                <Coins className="w-3 h-3" /> {credits} credits remaining
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
